import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WeeklySummary = ({ timeEntries, projects, selectedWeek, onWeekChange }) => {
  const getWeekDates = (weekStart) => {
    const dates = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date?.setDate(start?.getDate() + i);
      dates?.push(date?.toISOString()?.split('T')?.[0]);
    }
    return dates;
  };

  const weekDates = getWeekDates(selectedWeek);
  const weekEntries = timeEntries?.filter(entry => weekDates?.includes(entry?.date));

  const getDayTotal = (date) => {
    return weekEntries?.filter(entry => entry?.date === date)?.reduce((sum, entry) => sum + entry?.duration, 0);
  };

  const getBillableHours = (date) => {
    return weekEntries?.filter(entry => entry?.date === date && entry?.billable)?.reduce((sum, entry) => sum + entry?.duration, 0);
  };

  const getProjectHours = (projectId) => {
    return weekEntries?.filter(entry => entry?.projectId === projectId)?.reduce((sum, entry) => sum + entry?.duration, 0);
  };

  const totalWeekHours = weekEntries?.reduce((sum, entry) => sum + entry?.duration, 0);
  const totalBillableHours = weekEntries?.filter(entry => entry?.billable)?.reduce((sum, entry) => sum + entry?.duration, 0);
  const totalRevenue = weekEntries?.filter(entry => entry?.billable)?.reduce((sum, entry) => {
      const project = projects?.find(p => p?.id === entry?.projectId);
      return sum + (entry?.duration * (project?.hourlyRate || 0));
    }, 0);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatWeekRange = (weekStart) => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end?.setDate(start?.getDate() + 6);
    return `${start?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const navigateWeek = (direction) => {
    const currentWeek = new Date(selectedWeek);
    currentWeek?.setDate(currentWeek?.getDate() + (direction * 7));
    onWeekChange(currentWeek?.toISOString()?.split('T')?.[0]);
  };

  const projectSummary = projects?.map(project => ({
    ...project,
    weekHours: getProjectHours(project?.id),
    weekRevenue: weekEntries?.filter(entry => entry?.projectId === project?.id && entry?.billable)?.reduce((sum, entry) => sum + (entry?.duration * project?.hourlyRate), 0)
  }))?.filter(project => project?.weekHours > 0);

  return (
    <div className="bg-surface border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Weekly Summary</h2>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateWeek(-1)}
            >
              <Icon name="ChevronLeft" size={20} />
            </Button>
            <div className="text-sm font-medium text-foreground min-w-48 text-center">
              {formatWeekRange(selectedWeek)}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateWeek(1)}
            >
              <Icon name="ChevronRight" size={20} />
            </Button>
          </div>
        </div>

        {/* Week Stats */}
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">{totalWeekHours?.toFixed(1)}h</div>
            <div className="text-sm text-muted-foreground">Total Hours</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">{totalBillableHours?.toFixed(1)}h</div>
            <div className="text-sm text-muted-foreground">Billable Hours</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">${totalRevenue?.toFixed(0)}</div>
            <div className="text-sm text-muted-foreground">Revenue</div>
          </div>
        </div>
      </div>
      {/* Daily Breakdown */}
      <div className="p-6 border-b border-border">
        <h3 className="font-medium text-foreground mb-4">Daily Breakdown</h3>
        <div className="grid grid-cols-7 gap-2">
          {weekDates?.map((date, index) => {
            const dayTotal = getDayTotal(date);
            const dayBillable = getBillableHours(date);
            const isToday = date === new Date()?.toISOString()?.split('T')?.[0];
            
            return (
              <div
                key={date}
                className={`p-3 rounded-lg border text-center ${
                  isToday ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {formatDate(date)}
                </div>
                <div className="text-lg font-bold text-foreground">
                  {dayTotal?.toFixed(1)}h
                </div>
                <div className="text-xs text-success">
                  {dayBillable?.toFixed(1)}h billable
                </div>
                <div className="mt-2 w-full bg-muted rounded-full h-1">
                  <div
                    className="bg-primary h-1 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((dayTotal / 8) * 100, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Project Breakdown */}
      <div className="p-6">
        <h3 className="font-medium text-foreground mb-4">Project Breakdown</h3>
        <div className="space-y-3">
          {projectSummary?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-3" />
              <div className="text-muted-foreground">No time entries this week</div>
            </div>
          ) : (
            projectSummary?.map((project) => (
              <div key={project?.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20">
                <div className="flex-1">
                  <div className="font-medium text-sm text-foreground">{project?.name}</div>
                  <div className="text-xs text-muted-foreground">{project?.client}</div>
                </div>
                <div className="flex items-center space-x-6 text-sm">
                  <div className="text-right">
                    <div className="font-medium text-foreground">{project?.weekHours?.toFixed(1)}h</div>
                    <div className="text-xs text-muted-foreground">Hours</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-success">${project?.weekRevenue?.toFixed(0)}</div>
                    <div className="text-xs text-muted-foreground">Revenue</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-foreground">${project?.hourlyRate}/hr</div>
                    <div className="text-xs text-muted-foreground">Rate</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* AI Insights */}
      <div className="p-6 border-t border-border bg-primary/5">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Sparkles" size={16} color="white" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-foreground mb-2">AI Insights</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>• You're averaging {(totalWeekHours / 5)?.toFixed(1)} hours per day this week, which is {totalWeekHours >= 40 ? 'on track' : 'below'} your 8-hour target.</p>
              <p>• Your billable rate is {((totalBillableHours / totalWeekHours) * 100)?.toFixed(0)}% - consider optimizing non-billable tasks.</p>
              {projectSummary?.length > 0 && (
                <p>• {projectSummary?.[0]?.name} is your most active project this week with {projectSummary?.[0]?.weekHours?.toFixed(1)} hours logged.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklySummary;